import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { Subscription, firstValueFrom } from "rxjs";
import { ETRAINING_DB } from "src/app/app.module";
import { CONSTANT, RATING_FORM_UI_SCHEMA_VERSION, RATING_FORM_UI_VERSION_TS1_V3, RATING_FORM_UI_VERSION_TS1_V4 } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import {
	DraftItem,
	IndexedDb,
	Obserevations,
	SessionCfgRaw,
	SessionGroupOut,
	TS1FormResponseJSON,
} from "src/app/models/models";
import { ETrainingIdbService } from "src/app/shared/services/e-training-idb.service";
import { HttpService } from "src/app/shared/services/http.service";
import { MasterDataService } from "src/app/shared/services/masterdata.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { GoogleAuthService } from "src/app/services/google-api.service";
import { ContainerRatingsService } from "./services/base/container-ratings.services";
import {
	mergeDraftFrontendFieldsFromLocal,
	normalizeStagingApiEntryToDraft,
} from "./shared-v2/utils/draft-frontend-fields.utils";
import {
	hydrateFormDetailsConfigVersion,
	hydrateFormReminderNotes,
	hydrateFormSessionDetailsRemarks,
} from "./shared-v2/utils/form-config-version.utils";
import { clearTs1V3ObUndoBaselines } from "./shared-v2/utils/observation.utils";
import {
	resolveInitialRatingFormVersion,
	resolveRatingFormVersionForLoad,
	shouldPersistBackfilledRatingFormVersion,
} from "./shared-v2/utils/rating-form-version.utils";
import {
	bootstrapFormType,
	bootstrapRatingFormUiVersion,
	effectiveDraftForBootstrap,
	shouldClearRatingSessionState,
} from "./shared-v2/utils/rating-session-bootstrap.utils";
import {
	clearRatingSessionShell,
	readRatingSessionShell,
	writeRatingSessionShell,
} from "./shared-v2/utils/rating-session-persistence.utils";
import { resolveStagingPullConflict } from "./shared-v2/utils/staging-pull-merge.utils";

export type FormLoadState = "idle" | "loading" | "ready" | "error";

@Component({
	selector: "app-container-ratings-form",
	templateUrl: "./container-ratings-form.component.html",
	styleUrls: ["./container-ratings-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ContainerRatingsFormComponent implements OnInit, OnDestroy {
	private routeParamSubscription?: Subscription;

	formType: string = "";
	formData: any = {};
	observations: Obserevations[] = [];
	sessions: any;

	/** @deprecated Kept for template — v1 is no longer reachable. Always false. */
	legacy: boolean = false;

	readonly FORM_TYPE = CONSTANT.FORM_TYPE;

	/**
	 * Resolved from `DraftItem.ratingFormVersion`. Template uses `@switch` on this value.
	 */
	ratingFormUiVersion: number | null = null;

	/** `@case` for TS1 v3 (persisted `ratingFormVersion === 3`). */
	readonly ratingFormUiCaseTs1V3 = RATING_FORM_UI_VERSION_TS1_V3;

	/** `@case` for TS1 v4 (persisted `ratingFormVersion === 4`). */
	readonly ratingFormUiCaseTs1V4 = RATING_FORM_UI_VERSION_TS1_V4;

	/** `@case` for TS3/5/6/cabin v2 bundle (persisted `ratingFormVersion === 2`). */
	readonly ratingFormUiCaseV2 = RATING_FORM_UI_SCHEMA_VERSION;

	formLoadState: FormLoadState = "idle";
	formLoadError = "";
	/** When true, error CTA returns to pending list; otherwise ratings list. */
	private loadErrorIsDraft = false;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly httpService: HttpService,
		private readonly sharedService: SharedService,
		private readonly containerRatingsService: ContainerRatingsService,
		private readonly masterDataService: MasterDataService,
		private readonly roleService: RoleService,
		private readonly eTrainingIdb: ETrainingIdbService,
		private readonly googleAuth: GoogleAuthService,
		@Inject(ETRAINING_DB) private readonly eTrainingDb: NgxIndexedDBService,
	) {}

	ngOnInit(): void {
		this.initFromRouteParams();
	}

	ngOnDestroy(): void {
		this.routeParamSubscription?.unsubscribe();
		clearTs1V3ObUndoBaselines();
	}

	goBackFromLoadError(): void {
		void this.router.navigate([this.loadErrorIsDraft ? RouteType.PENDING : RouteType.RATINGS]);
	}

	private initFromRouteParams(): void {
		this.routeParamSubscription = this.route.params.subscribe((params) => {
			const id: string | undefined = params["id"];
			const draftId: string | undefined = params["draftId"];
			const routeKey = draftId ?? id ?? "";
			const activeRouteKey = this.containerRatingsService.getActiveRouteKey();

			if (shouldClearRatingSessionState(activeRouteKey, routeKey)) {
				this.ratingFormUiVersion = null;
				this.formType = "";
				clearTs1V3ObUndoBaselines();
				if (activeRouteKey) {
					clearRatingSessionShell(activeRouteKey);
				}
			}

			const sessionShell = readRatingSessionShell(routeKey);
			this.bootstrapRatingSessionFromService(routeKey, sessionShell);
			this.containerRatingsService.setActiveRouteKey(routeKey);

			if (draftId) {
				void this.fetchDraft(draftId);
				return;
			}

			if (id) {
				void this.fetchForm(id);
			}
		});
	}

	/**
	 * Restore shell routing synchronously after hot reload.
	 * `ContainerRatingsService` survives HMR; the container instance does not.
	 */
	private bootstrapRatingSessionFromService(
		routeKey: string,
		sessionShell: ReturnType<typeof readRatingSessionShell> = readRatingSessionShell(routeKey),
	): void {
		const draft = this.containerRatingsService.draft;
		const formSnapshot = this.containerRatingsService.getFormSnapshot();

		const bootstrappedFormType = bootstrapFormType({
			routeKey,
			activeRouteKey: this.containerRatingsService.getActiveRouteKey(),
			draft,
			serviceFormTypeCode: this.containerRatingsService.formType,
			formSnapshot,
			sessionShell,
		});
		if (bootstrappedFormType) {
			this.formType = bootstrappedFormType;
		}

		const bootstrappedVersion = bootstrapRatingFormUiVersion({
			routeKey,
			activeRouteKey: this.containerRatingsService.getActiveRouteKey(),
			draft,
			formTypeCode: this.containerRatingsService.formType,
			formSnapshot,
			sessionShell,
		});
		if (bootstrappedVersion != null) {
			this.setRatingFormUiVersion(routeKey, bootstrappedVersion);
		}
	}

	private setRatingFormUiVersion(routeKey: string, version: number): void {
		this.ratingFormUiVersion = version;
		writeRatingSessionShell({
			routeKey,
			formType: this.formType || undefined,
			ratingFormUiVersion: version,
		});
	}

	private beginLoad(isDraft: boolean): void {
		this.formLoadState = "loading";
		this.formLoadError = "";
		this.loadErrorIsDraft = isDraft;
	}

	private setLoadError(message: string, isDraft: boolean): void {
		this.loadErrorIsDraft = isDraft;
		this.formLoadError = message;
		this.formLoadState = "error";
	}

	/** Best-effort draft persist — never blocks loading on IndexedDB write failures. */
	private async persistDraft(draft: DraftItem): Promise<void> {
		try {
			await firstValueFrom(this.eTrainingDb.update(CONSTANT.INDEXEDDB.DRAFT, draft));
		} catch (err) {
			console.warn("Failed to persist draft to IndexedDB; continuing with in-memory copy:", err);
		}
	}

	private async readDraftById(draftId: string): Promise<DraftItem | undefined> {
		try {
			return await firstValueFrom(this.eTrainingDb.getByKey<DraftItem>(CONSTANT.INDEXEDDB.DRAFT, draftId));
		} catch (err) {
			console.error("Failed to read draft from IndexedDB:", err);
			return undefined;
		}
	}

	private async fetchDraft(draftId: string): Promise<void> {
		this.beginLoad(true);

		try {
			if (!(await this.ensureLoadPrerequisites(true))) {
				return;
			}

			if (navigator.onLine) {
				await this.refreshDraftFromApi(draftId);
			}

			await this.loadDraftFromETrainingDB(draftId);
		} catch (err) {
			console.error("Failed to load draft:", err);
			this.setLoadError("Unable to load this draft. Please try again from the pending list.", true);
		}
	}

	/** Pull staging draft when online. Errors are swallowed — local IndexedDB remains the source of truth. */
	private async refreshDraftFromApi(draftId: string): Promise<void> {
		try {
			const raw: any = await firstValueFrom(
				this.httpService.getFormInStaging({ role: this.roleService.getUserRole() }),
			);
			const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
			const apiDraft = list.find((entry: any) => normalizeStagingApiEntryToDraft(entry)?.id === draftId);
			if (!apiDraft) {
				return;
			}

			const serverDraft = normalizeStagingApiEntryToDraft(apiDraft);
			if (!serverDraft) {
				return;
			}

			const localDraft = await this.readDraftById(draftId);
			const decision = resolveStagingPullConflict(serverDraft as DraftItem, localDraft);

			if (decision.action === "keep_local") {
				if (decision.shouldSync) {
					await this.containerRatingsService.syncOneDraft(decision.draft);
				}
				return;
			}

			const formId =
				(serverDraft as DraftItem).trainingInformation?.formId ?? (serverDraft as DraftItem).formDetails?.id;
			let serverFormDetails = (serverDraft as DraftItem).formDetails;
			if (serverFormDetails) {
				serverFormDetails = await hydrateFormDetailsConfigVersion(
					serverFormDetails,
					formId,
					this.eTrainingDb,
					this.httpService,
				);
			}

			const mergedDraft = mergeDraftFrontendFieldsFromLocal(
				{
					...(serverDraft as DraftItem),
					formDetails: serverFormDetails,
					synced: true,
				},
				localDraft,
			);

			await this.persistDraft(mergedDraft);
		} catch (err) {
			console.warn("Failed to refresh draft from API, falling back to local:", err);
		}
	}

	private async loadDraftFromETrainingDB(draftId: string): Promise<void> {
		const draft = await this.readDraftById(draftId);
		if (!draft) {
			this.setLoadError("Draft not found. It may have been removed or is unavailable offline.", true);
			return;
		}

		const prepared = await this.prepareDraftForLoad(draft);
		this.applyDraftToService(prepared);
		this.formLoadState = "ready";
	}

	/** Hydrate form details + backfill ratingFormVersion once before mounting the shell. */
	private async prepareDraftForLoad(draft: DraftItem): Promise<DraftItem> {
		let working = draft;
		const formId = draft.trainingInformation?.formId ?? draft.formDetails?.id;

		if (draft.formDetails) {
			const hydrated = await hydrateFormDetailsConfigVersion(
				draft.formDetails,
				formId,
				this.eTrainingDb,
				this.httpService,
			);
			if (hydrated !== draft.formDetails) {
				working = { ...working, formDetails: hydrated };
			}
		}

		working = effectiveDraftForBootstrap(working, this.containerRatingsService.getFormSnapshot());

		if (shouldPersistBackfilledRatingFormVersion(working)) {
			const version = resolveRatingFormVersionForLoad(working);
			if (version != null) {
				working = { ...working, ratingFormVersion: version };
			}
		}

		if (working !== draft) {
			await this.persistDraft(working);
		}

		return working;
	}

	private applyDraftToService(draft: DraftItem): void {
		const formTypeCode = draft.formDetails?.formType?.code ?? CONSTANT.FORM_TYPE.ts_6;
		this.formType = this.mapFormType(formTypeCode);

		const resolved = resolveRatingFormVersionForLoad(draft);
		if (resolved != null) {
			this.setRatingFormUiVersion(draft.id, resolved);
		}

		this.containerRatingsService.setFormType(formTypeCode);
		this.containerRatingsService.inDraftMode = true;

		const fm = this.sharedService.formMasterData?.[formTypeCode];
		this.containerRatingsService.loadDraftItem(draft.id);
		this.containerRatingsService.setMinimumDate(draft.formDetails?.startDate);
		this.containerRatingsService.setInitialDate(draft.trainingInformation?.assessmentDate);
		this.containerRatingsService.setManueversRawData(fm?.maneuvers || []);
		this.containerRatingsService.setObservationData(this.transformObservation(fm));
		this.containerRatingsService.setFormData(draft.formDetails);
		this.containerRatingsService.setSessionData(this.transformSessions(fm?.sessionConfigurations ?? []));
	}

	/**
	 * IndexedDB + master-data caches must be ready before applying form/draft.
	 * Avoids blank child forms when refresh races with configure()'s master-data hydrate.
	 */
	private async ensureLoadPrerequisites(isDraft: boolean): Promise<boolean> {
		await this.eTrainingIdb.ensureInitialized();

		// InstructorGuard may admit this route before configure() finishes. Wait for session
		// hydrate first so master-data caches are filled by configure, then ensureReady shares
		// any in-flight init instead of racing a second fetch.
		if (!this.masterDataService.hasCachedMasterData() && navigator.onLine) {
			await this.googleAuth.ensureSessionResolved();
		}

		const masterReady = await this.masterDataService.ensureReady();
		if (!masterReady) {
			this.setLoadError(
				navigator.onLine
					? "Form configuration is not available yet. Please try again."
					: "Form configuration is not available offline. Reconnect once to cache it, then try again.",
				isDraft,
			);
			return false;
		}
		return true;
	}

	/** Load form for `/ratings/:id`: IndexedDB first, then API fallback when online. */
	private async fetchForm(formId: string): Promise<void> {
		this.beginLoad(false);

		try {
			if (!(await this.ensureLoadPrerequisites(false))) {
				return;
			}

			let form: IndexedDb<TS1FormResponseJSON> | undefined;
			if (navigator.onLine) {
				form = await this.fetchFormFromApi(formId);
			}
			if (!form?.data) {
				form = await this.readFormById(formId);
			}

			if (!form?.data) {
				this.setLoadError(
					navigator.onLine
						? "Form not found. It may have been removed or you no longer have access."
						: "Form is not available offline. Reconnect and try again, or open it from the ratings list first.",
					false,
				);
				return;
			}

			await this.applyFormData(formId, form.data);
			this.formLoadState = "ready";
		} catch (err) {
			console.error("Failed to load form:", err);
			this.setLoadError("Unable to load this form. Please try again from the ratings list.", false);
		}
	}

	private async readFormById(formId: string): Promise<IndexedDb<TS1FormResponseJSON> | undefined> {
		try {
			return await firstValueFrom(
				this.eTrainingDb.getByKey<IndexedDb<TS1FormResponseJSON>>(CONSTANT.INDEXEDDB.FORM, formId),
			);
		} catch (err) {
			console.error("Failed to read form from IndexedDB:", err);
			return undefined;
		}
	}

	private async fetchFormFromApi(formId: string): Promise<IndexedDb<TS1FormResponseJSON> | undefined> {
		try {
			const resp: any = await firstValueFrom(this.httpService.getForm(formId));
			const data = (resp?.data ?? resp) as TS1FormResponseJSON | undefined;
			if (!data || typeof data !== "object") {
				return undefined;
			}

			const record = {
				id: String(formId),
				data,
				stringify: JSON.stringify(data),
			};
			await this.upsertFormRecord(record);
			return record as IndexedDb<TS1FormResponseJSON>;
		} catch (err) {
			console.error("Failed to fetch form from API:", err);
			return undefined;
		}
	}

	private async upsertFormRecord(record: { id: string; data: TS1FormResponseJSON; stringify: string }): Promise<void> {
		try {
			await firstValueFrom(this.eTrainingDb.update(CONSTANT.INDEXEDDB.FORM, record));
		} catch {
			try {
				await firstValueFrom(this.eTrainingDb.add(CONSTANT.INDEXEDDB.FORM, record));
			} catch (err) {
				console.warn("Failed to cache form in IndexedDB after API fetch:", err);
			}
		}
	}

	private async applyFormData(formId: string, rawFormData: TS1FormResponseJSON): Promise<void> {
		let formData = await hydrateFormDetailsConfigVersion(rawFormData, formId, this.eTrainingDb, this.httpService);
		formData = await hydrateFormSessionDetailsRemarks(formData, formId, this.httpService);
		formData = await hydrateFormReminderNotes(formData, formId, this.httpService, this.eTrainingDb);

		const formTypeCode = formData?.formType?.code;
		this.formType = this.mapFormType(formTypeCode);
		this.containerRatingsService.setFormType(formTypeCode);

		const fm = this.sharedService.formMasterData?.[formTypeCode];
		this.containerRatingsService.setObservationData(this.transformObservation(fm));
		this.containerRatingsService.setSessionData(this.transformSessions(fm?.sessionConfigurations ?? []));
		this.containerRatingsService.setMinimumDate(formData.startDate);
		this.containerRatingsService.setInitialDate(new Date());
		this.containerRatingsService.setFormData(formData);
		this.containerRatingsService.setManueversRawData(fm?.maneuvers || []);

		const uiVersion = this.defaultRatingFormVersionForNewForm(formTypeCode, formData);
		if (uiVersion != null) {
			this.setRatingFormUiVersion(formId, uiVersion);
		}
	}

	transformObservation(fm: any) {
		const fmObs = fm?.observations ?? [];
		if (!fmObs.length) return [];

		const fmByCode = new Map<string, any>();
		for (const it of fmObs) {
			if (it?.code) fmByCode.set(it.code, it);
		}

		const all = this.sharedService.observations ?? [];
		const out = [];
		for (const o of all) {
			const match = fmByCode.get(o?.code);
			if (match) {
				out.push({ ...o, fixedManeuver: match });
			}
		}
		return out;
	}

	toCamel = (s: string) => (s ? s.replace(/_([a-z])/g, (_, c) => c.toUpperCase()) : s);

	transformSessions(cfgs: SessionCfgRaw[]): SessionGroupOut[] {
		const groups = new Map<string, SessionGroupOut>();

		for (const item of cfgs || []) {
			const t = item.session_conf_type ?? { code: "others", description: "Others" };
			const typeKey = t.code;
			if (!groups.has(typeKey)) {
				groups.set(typeKey, {
					name: {
						code: this.toCamel(t.code),
						description: t.description || "",
					},
					details: [],
				});
			}
			groups.get(typeKey)!.details.push({
				code: item.code,
				description: item.description,
			});
		}

		return Array.from(groups.values());
	}

	private defaultRatingFormVersionForNewForm(formTypeCode?: string, formDetails?: TS1FormResponseJSON): number | null {
		if (!formTypeCode) {
			return null;
		}
		const ft = this.mapFormType(formTypeCode);
		return resolveInitialRatingFormVersion(ft, formDetails);
	}

	private mapFormType(code?: string): string {
		if (!code) return "";

		const key = code.toString().toUpperCase().replace("_", "");
		const mapped = (CONSTANT.FORM_TYPE as Record<string, string>)[key];
		if (mapped) return mapped;

		const values = new Set(Object.values(CONSTANT.FORM_TYPE));
		if (values.has(code as any)) return code;

		return "";
	}
}
